import { z } from 'zod';
import type { ErrorCode } from './errors';

/**
 * Typed route contracts. Every JSON route under /api is one `defineRoute(...)` object in
 * shared/src/api/<module>.ts. The server binds a handler to it (core checks auth, the
 * install gate, parses params/query/body with the zod schemas) and the client calls it
 * through `createApiClient(...).call(route, input)`. Both sides share the types.
 *
 * Rules for request schemas: no `.transform()` or `.pipe()` in `body` (the client sends
 * what it validated), and query values arrive as strings, so use the `q*` helpers in
 * schemas/common.ts for numbers and booleans.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * - pub: no session needed.
 * - mem: any signed-in member. memw is the same in v1 (there is no guest role, R10);
 *   it marks routes that write shared crew data, so a guest role could be added later.
 * - own: the crew owner.
 * - sk: a signed-in member; the service checks that they hold the book (keeper) or own.
 */
export type AuthLevel = 'pub' | 'mem' | 'memw' | 'own' | 'sk';

/**
 * - json: JSON in, JSON out (the default).
 * - multipart: multipart/form-data in, JSON out. The client passes `form`.
 * - sse: text/event-stream out; `res` is the zod union of the stream's messages.
 * - file: a download (backup files). `res` is z.unknown(); use `client.url()`.
 */
export type RouteKind = 'json' | 'multipart' | 'sse' | 'file';
export type SuccessStatus = 200 | 201 | 202 | 204;

type AnyZod = z.ZodType;

type CleanParam<S extends string> = S extends `${infer N}.${string}` ? N : S;
/** Names of `:param` segments in a path literal. */
export type ParamNames<P extends string> = P extends `${string}:${infer Rest}`
  ? Rest extends `${infer Name}/${infer Tail}`
    ? CleanParam<Name> | ParamNames<`/${Tail}`>
    : CleanParam<Rest>
  : never;
export type PathParams<P extends string> = { [K in ParamNames<P>]: string };

export interface RouteDef<
  M extends HttpMethod = HttpMethod,
  P extends string = string,
  Q extends AnyZod | undefined = AnyZod | undefined,
  B extends AnyZod | undefined = AnyZod | undefined,
  R extends AnyZod = AnyZod,
  K extends RouteKind = RouteKind,
> {
  /** Stable dotted name, unique across the app: `games.postEvents`. */
  name: string;
  method: M;
  /** Absolute path with Hono-style params: `/api/games/:id/events`. */
  path: P;
  auth: AuthLevel;
  query?: Q;
  body?: B;
  res: R;
  kind?: K;
  status?: SuccessStatus;
  /**
   * Install gate (R11): when true, a request without `X-TTS-Display: standalone` gets
   * 428 INSTALL_REQUIRED. Defaults to true for every auth level except `pub`.
   */
  gate?: boolean;
  /** Error codes this route can answer besides the generic ones. Docs and tests only. */
  errors?: readonly ErrorCode[];
  /** Creates keyed by a client uuidv7 `id`: a replay answers the stored row, not 409. */
  idempotent?: boolean;
  /** Request body cap in bytes when it differs from core's default (64 KB JSON). */
  maxBodyBytes?: number;
  summary?: string;
}

export interface RouteContract<
  M extends HttpMethod = HttpMethod,
  P extends string = string,
  Q extends AnyZod | undefined = AnyZod | undefined,
  B extends AnyZod | undefined = AnyZod | undefined,
  R extends AnyZod = AnyZod,
  K extends RouteKind = RouteKind,
> {
  readonly name: string;
  readonly method: M;
  readonly path: P;
  readonly auth: AuthLevel;
  readonly query: Q;
  readonly body: B;
  readonly res: R;
  readonly kind: K;
  readonly status: SuccessStatus;
  readonly gate: boolean;
  readonly errors: readonly ErrorCode[];
  readonly idempotent: boolean;
  readonly maxBodyBytes: number | undefined;
  readonly summary: string | undefined;
  /** Param names in path order. */
  readonly params: readonly string[];
  /** zod object for the path params (all non-empty strings). */
  readonly paramsSchema: z.ZodObject<Record<string, z.ZodString>>;
}

// biome-ignore lint/suspicious/noExplicitAny: a registry of contracts of every shape
export type AnyRoute = RouteContract<HttpMethod, string, any, any, any, RouteKind>;

const PARAM_RE = /:([A-Za-z_][A-Za-z0-9_]*)/g;

export function pathParamNames(path: string): string[] {
  return Array.from(path.matchAll(PARAM_RE), (m) => m[1] as string);
}

export function defineRoute<
  const M extends HttpMethod,
  const P extends string,
  Q extends AnyZod | undefined = undefined,
  B extends AnyZod | undefined = undefined,
  R extends AnyZod = AnyZod,
  const K extends RouteKind = 'json',
>(def: RouteDef<M, P, Q, B, R, K>): RouteContract<M, P, Q, B, R, K> {
  if (!def.path.startsWith('/')) throw new Error(`defineRoute ${def.name}: path must be absolute`);
  if (def.body && (def.method === 'GET' || def.kind === 'multipart')) {
    throw new Error(`defineRoute ${def.name}: GET and multipart routes take no JSON body`);
  }
  const params = pathParamNames(def.path);
  const shape: Record<string, z.ZodString> = {};
  for (const p of params) shape[p] = z.string().min(1).max(200);
  return Object.freeze({
    name: def.name,
    method: def.method,
    path: def.path,
    auth: def.auth,
    query: def.query as Q,
    body: def.body as B,
    res: def.res,
    kind: (def.kind ?? 'json') as K,
    status: def.status ?? 200,
    gate: def.gate ?? def.auth !== 'pub',
    errors: def.errors ?? [],
    idempotent: def.idempotent ?? false,
    maxBodyBytes: def.maxBodyBytes,
    summary: def.summary,
    params,
    paramsSchema: z.object(shape),
  });
}

/** What the caller passes for a route: params, query, body and form only when the route has them. */
export type RouteInput<C extends AnyRoute> = ([ParamNames<C['path']>] extends [never]
  ? { params?: undefined }
  : { params: PathParams<C['path']> }) &
  (C['query'] extends AnyZod ? { query?: z.input<C['query']> } : { query?: undefined }) &
  (C['body'] extends AnyZod ? { body: z.input<C['body']> } : { body?: undefined }) &
  (C['kind'] extends 'multipart' ? { form: unknown } : { form?: undefined });

/** Parsed (server side) request parts. */
export interface RouteRequest<C extends AnyRoute> {
  params: PathParams<C['path']>;
  query: C['query'] extends AnyZod ? z.output<C['query']> : undefined;
  body: C['body'] extends AnyZod ? z.output<C['body']> : undefined;
}

export type RouteRes<C extends AnyRoute> = z.output<C['res']>;
/** The JSON a handler returns before the response schema parses it. */
export type RouteResInput<C extends AnyRoute> = z.input<C['res']>;

/**
 * Non-contract routes that live outside /api (or speak a protocol of their own): /mcp,
 * /oauth/*, /.well-known/*, /cal/*, /j/:code, /media/*, /healthz. They are listed so the
 * server can assert at boot that each one is bound exactly once and that the SPA
 * fallback never swallows them.
 */
export type RawRouteAuth = 'pub' | 'cookie' | 'bearer' | 'token';
export interface RawRoute {
  readonly name: string;
  readonly method: HttpMethod;
  readonly path: string;
  readonly auth: RawRouteAuth;
  /** Module that binds it through `ServerModule.rawRoutes`. */
  readonly owner: string;
  readonly maxBodyBytes?: number;
  readonly summary?: string;
}

export function defineRawRoute(r: RawRoute): RawRoute {
  if (r.path.startsWith('/api/')) throw new Error(`defineRawRoute ${r.name}: /api paths must be contracts`);
  return Object.freeze({ ...r });
}

/** Key used to detect duplicate bindings: `POST /api/games/:id/events`. */
export function routeKey(r: { method: string; path: string }): string {
  return `${r.method} ${r.path.replace(PARAM_RE, ':_')}`;
}

/**
 * Order for binding routes in a first-match router (Hono matches in registration order):
 * at the first segment where two paths differ, a static segment wins over a `:param`, so
 * GET /api/sessions/next is bound before GET /api/sessions/:id. Sort with this before binding.
 */
export function compareRoutePaths(a: { path: string }, b: { path: string }): number {
  const sa = a.path.split('/');
  const sb = b.path.split('/');
  const n = Math.min(sa.length, sb.length);
  for (let i = 0; i < n; i++) {
    const x = sa[i] as string;
    const y = sb[i] as string;
    if (x === y) continue;
    const px = x.startsWith(':');
    const py = y.startsWith(':');
    if (px !== py) return px ? 1 : -1;
    return x < y ? -1 : 1;
  }
  return sa.length - sb.length;
}

/** Collect every contract exported by a module's api file (any value with method+path+res). */
export function contractsOf(mod: Record<string, unknown>): AnyRoute[] {
  const out: AnyRoute[] = [];
  for (const v of Object.values(mod)) {
    if (v && typeof v === 'object' && 'method' in v && 'path' in v && 'res' in v && 'paramsSchema' in v) {
      out.push(v as AnyRoute);
    }
  }
  return out;
}

export function rawRoutesOf(mod: Record<string, unknown>): RawRoute[] {
  const out: RawRoute[] = [];
  for (const v of Object.values(mod)) {
    if (v && typeof v === 'object' && 'method' in v && 'path' in v && 'owner' in v && !('res' in v)) {
      out.push(v as RawRoute);
    }
  }
  return out;
}
